/*
  The words of this module's screens (plain engineering words, core/plain-words.md).
  One place, so a label is changed once. Nothing here is ontology content: these are the app's own labels
  for the kinds, states and answers named in the contracts.
*/

import type { InboxEntry, MatchWord, TestResultWord } from "../../../api/types";
import { humanise } from "../../../ui/words";

// The kinds of element and of line are said the same way on every screen (UI-25): the tables are ui/words.ts.
export { ELEMENT_KIND_WORDS, LINE_KIND_WORDS, elementKindWord, lineKindWord, humanise } from "../../../ui/words";

/** What the inbox calls the reason an element waits (BR-13: the light Next version is the "one-click check"). */
export const KIND_OF_WAIT_WORDS: Record<InboxEntry["kind_of_wait"], string> = {
  new: "New",
  next_version: "Next version",
  light_next_version: "One-click check",
  removal: "Marked for removal",
  reworked: "Reworked after it was sent back",
  follow_up_lines: "New lines",
};

export const MATCH_WORDS: Record<MatchWord, string> = {
  the_same: "The same",
  theirs_is_wider: "Theirs is wider",
  theirs_is_narrower: "Theirs is narrower",
  nearly_the_same: "Nearly the same",
  only_related: "Only related",
  no_match: "No match",
};

export function matchWord(word: string): string {
  return (MATCH_WORDS as Record<string, string>)[word] ?? humanise(word);
}

export const MODULE_STATUS_WORDS: Record<string, string> = {
  not_started: "Not started",
  started: "Started",
  done: "Done",
  skipped: "Skipped",
  known: "Known (skipped by showing)",
};

export function moduleStatusWord(status: string): string {
  return MODULE_STATUS_WORDS[status] ?? humanise(status);
}

export const CONCEPT_LEVELS = ["not_seen", "explained", "practised", "shown"] as const;
export type ConceptLevel = (typeof CONCEPT_LEVELS)[number];

export const CONCEPT_LEVEL_WORDS: Record<ConceptLevel, string> = {
  not_seen: "Not seen",
  explained: "Explained",
  practised: "Practised",
  shown: "Shown",
};

export const DETAIL_LEVEL_WORDS = {
  short: "Short: the key sentence and an example",
  normal: "Normal: a short paragraph with one example",
  full: "Full: the reasoning in steps",
} as const;

export const EXPERIENCE_WORDS = {
  no: "No",
  a_little: "A little",
  yes: "Yes",
} as const;

export const ROLE_WORDS: Record<string, string> = {
  engineer: "Engineer",
  sme: "SME (subject matter expert)",
  engineer_and_sme: "Engineer and SME",
  admin: "Admin",
};

/** The answers of Test connection in plain words (architecture 7.7). The service's own message follows them. */
export const TEST_RESULT_WORDS: Record<TestResultWord, { headline: string; ok: boolean }> = {
  connected: { headline: "Connected. The model answered.", ok: true },
  key_refused: { headline: "The service refused the key.", ok: false },
  url_not_reached: { headline: "The address could not be reached.", ok: false },
  model_not_found: { headline: "The service does not know this model name.", ok: false },
  something_else: { headline: "Something else went wrong.", ok: false },
};

/** The first characters of a long text, with a mark when it was cut. */
export function cut(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}
